import React, { useState, useCallback, useMemo, useEffect } from "react";
import threadApi from "../../api/threadApi";

/**
 * ReplyItem Component
 *
 * Displays individual reply content with author information, timestamps,
 * like counts, and interaction buttons. Similar to MessageItem but optimized for replies.
 * Requirements: 3.4, 4.3, 7.1
 *
 * @param {Object} props - Component props
 * @param {Object} props.reply - Reply data object
 * @param {Object} props.currentUser - Current user data for permissions
 * @param {Function} props.onInteraction - Callback for reply interactions
 * @param {boolean} props.isFirst - Whether this is the first reply in list
 * @param {boolean} props.isLast - Whether this is the last reply in list
 * @param {boolean} props.isHighlighted - Whether this reply should be highlighted
 * @param {string} props.className - Additional CSS classes
 */
const ReplyItem = ({
  reply,
  currentUser,
  onInteraction,
  isFirst = false,
  isLast = false,
  isHighlighted = false,
  className = "",
}) => {
  // Local state for interaction feedback
  const [interactionState, setInteractionState] = useState({
    isLiking: false,
    likeError: null,
  });

  // Highlight state for notification-based highlighting
  const [highlightState, setHighlightState] = useState({
    isHighlighted: false,
    isAnimating: false,
  });

  /**
   * Handle highlight effect when reply is highlighted via notification
   */
  useEffect(() => {
    if (isHighlighted) {
      console.log("🎯 ReplyItem highlight effect triggered:", {
        replyId: reply._id,
        isHighlighted,
        replyContent: reply.content?.substring(0, 50) + "...",
      });

      // Start highlight animation
      setHighlightState({
        isHighlighted: true,
        isAnimating: true,
      });

      // Scroll to this reply
      const replyElement = document.getElementById(`reply-${reply._id}`);
      if (replyElement) {
        console.log("📍 Scrolling to reply:", reply._id);
        replyElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else {
        console.warn("⚠️ Reply element not found for scrolling:", reply._id);
      }

      // Remove highlight after 2 seconds
      const timer = setTimeout(() => {
        console.log("⏰ Removing highlight for reply:", reply._id);
        setHighlightState({
          isHighlighted: false,
          isAnimating: true,
        });

        // Remove animation class after fade completes
        setTimeout(() => {
          setHighlightState({
            isHighlighted: false,
            isAnimating: false,
          });
        }, 500); // Match CSS transition duration
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [isHighlighted, reply._id, reply.content]);

  /**
   * Check if current user is the reply author
   */
  const isOwnReply = useMemo(() => {
    return (
      currentUser &&
      reply.author &&
      (currentUser._id === reply.author._id ||
        currentUser.auth0Id === reply.author.auth0Id)
    );
  }, [currentUser, reply.author]);

  /**
   * Check if current user has liked this reply
   */
  const isLikedByUser = useMemo(() => {
    if (!currentUser) {
      return false;
    }

    // Check if the reply has the isLiked property (from API response)
    if (typeof reply.isLiked === "boolean") {
      return reply.isLiked;
    }

    // Check if the reply has the hasUserLiked property (from API response)
    if (typeof reply.hasUserLiked === "boolean") {
      return reply.hasUserLiked;
    }

    // Fallback to checking the likes array
    if (Array.isArray(reply.likes)) {
      return (
        reply.likes.includes(currentUser._id) ||
        reply.likes.includes(currentUser.auth0Id)
      );
    }

    return false;
  }, [currentUser, reply.isLiked, reply.hasUserLiked, reply.likes]);

  /**
   * Update interaction state
   */
  const updateInteractionState = useCallback((updates) => {
    setInteractionState((prev) => ({ ...prev, ...updates }));
  }, []);

  /**
   * Handle like/unlike action with optimistic updates and rollback
   */
  const handleLike = useCallback(async () => {
    if (!currentUser || isOwnReply || interactionState.isLiking) {
      return;
    }

    updateInteractionState({
      isLiking: true,
      likeError: null,
    });

    try {
      // Store original state for potential rollback
      const originalIsLiked = isLikedByUser;
      const originalLikeCount = reply.likeCount;

      // Optimistic update
      const newIsLiked = !originalIsLiked;
      const newLikeCount = newIsLiked
        ? originalLikeCount + 1
        : Math.max(0, originalLikeCount - 1);

      // Notify parent immediately for optimistic update
      if (onInteraction) {
        onInteraction(reply._id, "like", {
          isLiked: newIsLiked,
          likeCount: newLikeCount,
          optimistic: true,
        });
      }

      // Make actual API call
      const response = await threadApi.likeReply(reply._id);

      // Update with actual server response
      if (onInteraction) {
        onInteraction(reply._id, "like", {
          isLiked: response.liked,
          likeCount: response.likeCount,
          optimistic: false,
          serverResponse: response,
        });
      }

      updateInteractionState({ isLiking: false });
    } catch (error) {
      console.error("Failed to like reply:", error);

      // Rollback optimistic update
      if (onInteraction) {
        onInteraction(reply._id, "like", {
          isLiked: isLikedByUser,
          likeCount: reply.likeCount,
          rollback: true,
        });
      }

      // Handle specific error types
      let errorMessage = "Failed to like reply";
      if (error.type === "AUTH_ERROR") {
        errorMessage = "Please log in to like replies";
      } else if (error.type === "CLIENT_ERROR" && error.status === 409) {
        errorMessage = "You cannot like your own reply";
      } else if (error.type === "RATE_LIMIT_ERROR") {
        errorMessage = "You're liking too quickly. Please wait a moment.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      updateInteractionState({
        isLiking: false,
        likeError: errorMessage,
      });

      // Clear error after 5 seconds
      setTimeout(() => {
        updateInteractionState({ likeError: null });
      }, 5000);
    }
  }, [
    currentUser,
    isOwnReply,
    isLikedByUser,
    reply,
    onInteraction,
    interactionState.isLiking,
    updateInteractionState,
  ]);

  /**
   * Handle reply menu actions (edit, delete, etc.)
   */
  const handleMenuAction = useCallback(
    (action) => {
      if (onInteraction) {
        onInteraction(reply._id, action, { reply });
      }
    },
    [reply, onInteraction]
  );

  /**
   * Format reply content with basic text processing
   */
  const formatReplyContent = useCallback((content) => {
    if (!content) return "";

    // Basic text processing - preserve line breaks
    return content.split("\n").map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < content.split("\n").length - 1 && <br />}
      </React.Fragment>
    ));
  }, []);

  /**
   * Get author display name
   */
  const getAuthorDisplayName = useCallback(() => {
    if (!reply.author) return "Unknown User";
    return reply.author.name || reply.author.username || "Unknown User";
  }, [reply.author]);

  /**
   * Get author profile picture URL
   */
  const getAuthorPicture = useCallback(() => {
    return reply.author?.picture || null;
  }, [reply.author]);

  /**
   * Render author avatar (smaller for replies)
   */
  const renderAuthorAvatar = useCallback(() => {
    const picture = getAuthorPicture();
    const displayName = getAuthorDisplayName();
    const initials = displayName
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

    if (picture) {
      return (
        <img
          src={picture}
          alt={`${displayName}'s avatar`}
          className="w-8 h-8 rounded-full object-cover"
          onError={(e) => {
            // Fallback to initials if image fails to load
            e.target.style.display = "none";
            e.target.nextSibling.style.display = "flex";
          }}
        />
      );
    }

    return (
      <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-gray-700 font-medium text-xs">
        {initials}
      </div>
    );
  }, [getAuthorPicture, getAuthorDisplayName]);

  return (
    <div
      id={`reply-${reply._id}`}
      className={`reply-item ${className} transition-all duration-500 ease-in-out ${
        highlightState.isAnimating ? "transition-all duration-500" : ""
      }`}
      style={{
        backgroundColor: highlightState.isHighlighted
          ? "#FFF8E1"
          : "transparent", // light-gold background
        borderLeft: highlightState.isHighlighted
          ? "3px solid #f6a332"
          : "3px solid transparent", // gold border
        borderRadius: highlightState.isHighlighted ? "8px" : "0px",
        boxShadow: highlightState.isHighlighted
          ? "0 4px 12px rgba(246, 163, 50, 0.15)"
          : "none", // subtle gold shadow
        padding: highlightState.isHighlighted ? "8px" : "0px",
        margin: highlightState.isHighlighted ? "4px 0" : "0",
      }}
    >
      <div className="flex space-x-3">
        {/* Author Avatar (smaller for replies) */}
        <div className="flex-shrink-0">
          {renderAuthorAvatar()}
          {/* Fallback avatar (hidden by default) */}
          <div
            className="w-8 h-8 rounded-full bg-gray-300 items-center justify-center text-gray-700 font-medium text-xs"
            style={{ display: "none" }}
          >
            {getAuthorDisplayName()
              .split(" ")
              .map((n) => n[0])
              .join("")
              .toUpperCase()
              .slice(0, 2)}
          </div>
        </div>

        {/* Reply Content */}
        <div className="flex-1 min-w-0">
          {/* Reply Text with Author Name Inline */}
          <div className="text-gray-900 text-sm mb-1">
            <span className="font-medium mr-2">{getAuthorDisplayName()}</span>
            <span className="whitespace-pre-wrap break-words">
              {formatReplyContent(reply.content)}
            </span>
          </div>

          {/* Interaction Bar - Instagram style */}
          <div className="flex items-center space-x-4 text-xs text-gray-500">
            <span>{reply.displayTime || "Unknown time"}</span>

            {reply.likeCount > 0 && (
              <span className="font-medium">
                {reply.likeCount} {reply.likeCount === 1 ? "like" : "likes"}
              </span>
            )}

            {/* Like Button */}
            <button
              onClick={handleLike}
              disabled={!currentUser || isOwnReply || interactionState.isLiking}
              className={`flex items-center space-x-1 font-medium transition-colors ${
                !currentUser || isOwnReply
                  ? "opacity-50 cursor-not-allowed"
                  : interactionState.isLiking
                  ? "opacity-75 cursor-wait"
                  : isLikedByUser
                  ? "text-red-600 hover:text-red-700"
                  : "text-gray-500 hover:text-red-600"
              }`}
              title={
                !currentUser
                  ? "Login to like replies"
                  : isOwnReply
                  ? "You cannot like your own reply"
                  : interactionState.isLiking
                  ? "Processing..."
                  : isLikedByUser
                  ? "Unlike this reply"
                  : "Like this reply"
              }
            >
              {/* Heart Icon (smaller for replies) */}
              <svg
                className={`w-3 h-3 transition-all ${
                  isLikedByUser ? "fill-current" : "fill-none"
                } ${interactionState.isLiking ? "animate-pulse" : ""}`}
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={isLikedByUser ? 0 : 2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
              <span>
                {interactionState.isLiking
                  ? "..."
                  : isLikedByUser
                  ? "Liked"
                  : "Like"}
              </span>
            </button>

            {/* Reply Menu (for own replies) */}
            {isOwnReply && (
              <button
                onClick={() => handleMenuAction("menu")}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title="Reply options"
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Error Messages */}
          {interactionState.likeError && (
            <div className="mt-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded-lg">
              {interactionState.likeError}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReplyItem;
